import React, {useEffect, useState, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {inventoriesAPI} from '../../services/garageApi';

const InventoriesOverview = () => {
    const navigate = useNavigate();
    const [inventories, setInventories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({
        category: '',
        lowStock: false,
        search: ''
    });

    const fetchInventories = useCallback(async (page = currentPage) => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page,
                per_page: 20,
            };

            if (filters.category) {
                params.product_type = filters.category;
            }
            if (filters.lowStock) {
                params.low_stock = 1;
            }
            if (filters.search) {
                params.search = filters.search;
            }

            const response = await inventoriesAPI.getAll(params);
            const items = response.data || [];
            setInventories(items);
            setPagination(response.pagination || null);

        } catch (err) {
            console.error('Error fetching inventories:', err);
            setError('Failed to load inventory data: ' + err.message);
            setInventories([]);
        } finally {
            setLoading(false);
        }
    }, [filters, currentPage]);

    useEffect(() => {
        fetchInventories();
    }, [fetchInventories]);

    const getProductName = (item) => {
        const product = item.inventoryable;
        if (!product) return 'Unknown Product';

        switch (item.inventoryable_type) {
            case 'tire': {
                const brand = product.tire_brand?.name || '';
                const size = product.size || '';
                const type = product.tire_type?.name || '';
                const pattern = product.pattern?.name || '';
                return [brand, size, type, pattern].filter(Boolean).join(' ') || 'Tire';
            }
            case 'battery': {
                return product.battery_name || 'Battery';
            }
            case 'wiper': {
                const size = product.size || '';
                const type = product.type || '';
                return [size ? size + '"' : '', type, 'Wiper'].filter(Boolean).join(' ');
            }
            default:
                return 'Unknown Product';
        }
    };

    const getProductDetails = (item) => {
        const product = item.inventoryable;
        if (!product) return '';

        switch (item.inventoryable_type) {
            case 'tire':
                return [product.dot ? `DOT: ${product.dot}` : '', product.construction_type].filter(Boolean).join(' | ');
            case 'battery':
                return [product.ampere ? `${product.ampere}Ah` : '', product.battery_type, product.battery_shape].filter(Boolean).join(' | ');
            case 'wiper':
                return [product.frame_material, product.compatible_cars].filter(Boolean).join(' | ');
            default:
                return '';
        }
    };

    const getProductBrand = (item) => {
        const product = item.inventoryable;
        if (!product) return '';

        switch (item.inventoryable_type) {
            case 'tire':
                return product.tire_brand?.name || '';
            case 'battery':
                return product.battery_brand?.name || '';
            case 'wiper':
                return product.made_in?.name || '';
            default:
                return '';
        }
    };

    const getEditPath = (item) => {
        const product = item.inventoryable;
        if (!product) return '#';

        switch (item.inventoryable_type) {
            case 'tire':
                return `/tires/${product.id}/edit`;
            case 'battery':
                return `/batteries/${product.id}/edit`;
            case 'wiper':
                return `/wiper-products/edit/${product.id}`;
            default:
                return '#';
        }
    };

    const getCostPrice = (item) => {
        return parseFloat(item.inventoryable?.cost_price || 0);
    };

    const getSellingPrice = (item) => {
        return parseFloat(item.inventoryable?.selling_price || 0);
    };

    const isLowStock = (item) => {
        if (item.quantity === 0) return true;
        if (item.reorder_point && item.quantity <= item.reorder_point) return true;
        return false;
    };

    const getCategoryColor = (type) => {
        switch (type) {
            case 'battery':
                return 'bg-blue-100 text-blue-800';
            case 'tire':
                return 'bg-green-100 text-green-800';
            case 'wiper':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getCategoryLabel = (type) => {
        switch (type) {
            case 'battery': return 'Battery';
            case 'tire': return 'Tire';
            case 'wiper': return 'Wiper';
            default: return type;
        }
    };

    const handleFilterChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchInventories(page);
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="text-lg">Loading inventory data...</div>
        </div>
    );

    // Calculate summary stats
    const totalItems = pagination?.total || inventories.length;
    const lowStockItems = inventories.filter(item => isLowStock(item)).length;
    const totalValue = inventories.reduce((sum, item) => sum + (item.quantity * getCostPrice(item)), 0);
    const categoryCounts = inventories.reduce((acc, item) => {
        const cat = item.inventoryable_type || 'unknown';
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="space-y-6">
            {/* Error Messages */}
            {error && (
                <div className="rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                    {error}
                </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="rounded-lg bg-white p-6 shadow">
                    <div className="text-sm font-medium text-gray-500">Total Records</div>
                    <div className="text-2xl font-bold text-gray-900">{totalItems}</div>
                </div>
                <div className="rounded-lg bg-white p-6 shadow">
                    <div className="text-sm font-medium text-gray-500">Low Stock Alerts</div>
                    <div className="text-2xl font-bold text-red-600">{lowStockItems}</div>
                </div>
                <div className="rounded-lg bg-white p-6 shadow">
                    <div className="text-sm font-medium text-gray-500">Total Value (Cost)</div>
                    <div className="text-2xl font-bold text-green-600">${totalValue.toFixed(2)}</div>
                </div>
                <div className="rounded-lg bg-white p-6 shadow">
                    <div className="text-sm font-medium text-gray-500">By Type</div>
                    <div className="text-sm text-gray-600">
                        Tires: {categoryCounts.tire || 0}<br/>
                        Batteries: {categoryCounts.battery || 0}<br/>
                        Wipers: {categoryCounts.wiper || 0}
                    </div>
                </div>
            </div>

            {/* Main Inventory Table */}
            <div className="rounded-lg bg-white shadow">
                {/* Header */}
                <div className="border-b border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <h5 className="text-lg font-semibold">Inventory Overview</h5>
                        <div className="flex space-x-2">
                            <button
                                className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                                onClick={() => navigate('/batteries/create')}
                            >
                                Add Battery
                            </button>
                            <button
                                className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                                onClick={() => navigate('/tires/create')}
                            >
                                Add Tire
                            </button>
                            <button
                                className="rounded-md bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
                                onClick={() => navigate('/wiper-products/create')}
                            >
                                Add Wiper
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="border-b border-gray-200 p-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                        <input
                            type="text"
                            name="search"
                            placeholder="Search products..."
                            value={filters.search}
                            onChange={handleFilterChange}
                            className="rounded-md border-gray-300 px-3 py-2"
                        />
                        <select
                            name="category"
                            value={filters.category}
                            onChange={handleFilterChange}
                            className="rounded-md border-gray-300 px-3 py-2"
                        >
                            <option value="">All Categories</option>
                            <option value="battery">Batteries</option>
                            <option value="tire">Tires</option>
                            <option value="wiper">Wipers</option>
                        </select>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                name="lowStock"
                                checked={filters.lowStock}
                                onChange={handleFilterChange}
                                className="rounded border-gray-300"
                            />
                            <span className="ml-2">Low Stock Only</span>
                        </label>
                        <button
                            onClick={() => fetchInventories(1)}
                            className="rounded-md bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
                        >
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Product
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Location / Zone
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Stock
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Pricing
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                        {inventories.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                                    No inventory items found
                                </td>
                            </tr>
                        ) : (
                            inventories.map((item) => (
                                <tr key={item.id} className={isLowStock(item) ? 'bg-red-50' : ''}>
                                    <td className="px-6 py-4">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {getProductName(item)}
                                            </div>
                                            {getProductBrand(item) && (
                                                <div className="text-sm text-gray-500">
                                                    {item.inventoryable_type === 'wiper' ? 'Origin' : 'Brand'}: {getProductBrand(item)}
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-400">{getProductDetails(item)}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getCategoryColor(item.inventoryable_type)}`}>
                                            {getCategoryLabel(item.inventoryable_type)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <div>{item.location?.name || '-'}</div>
                                        {item.zone && !item.zone.is_system && (
                                            <div className="text-xs text-gray-400">{item.zone.name}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900">
                                            Qty: {item.quantity}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            Available: {item.available_quantity ?? item.quantity} | Reserved: {item.reserved_quantity || 0}
                                        </div>
                                        {item.reorder_point && (
                                            <div className="text-xs text-gray-400">
                                                Reorder at: {item.reorder_point}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900">
                                            ${getSellingPrice(item).toFixed(2)}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Value: ${(item.quantity * getCostPrice(item)).toFixed(2)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                            item.quantity === 0
                                                ? 'bg-red-100 text-red-800'
                                                : isLowStock(item)
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-green-100 text-green-800'
                                        }`}>
                                            {item.quantity === 0 ? 'Out of Stock' : isLowStock(item) ? 'Low Stock' : 'In Stock'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm font-medium">
                                        <button
                                            className="mr-2 text-indigo-600 hover:text-indigo-900"
                                            onClick={() => navigate(getEditPath(item))}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="text-blue-600 hover:text-blue-900"
                                            onClick={() => navigate('/stock-adjustments/create')}
                                        >
                                            Adjust
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination && pagination.last_page > 1 && (
                    <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
                        <div className="text-sm text-gray-500">
                            Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
                        </div>
                        <div className="flex space-x-1">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage <= 1}
                                className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50 hover:bg-gray-50"
                            >
                                Previous
                            </button>
                            {Array.from({length: pagination.last_page}, (_, i) => i + 1)
                                .filter(page => {
                                    return page === 1 || page === pagination.last_page ||
                                        Math.abs(page - currentPage) <= 2;
                                })
                                .map((page, idx, arr) => (
                                    <React.Fragment key={page}>
                                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                                            <span className="px-2 py-1 text-gray-400">...</span>
                                        )}
                                        <button
                                            onClick={() => handlePageChange(page)}
                                            className={`rounded-md border px-3 py-1 text-sm ${
                                                currentPage === page
                                                    ? 'border-blue-500 bg-blue-500 text-white'
                                                    : 'border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    </React.Fragment>
                                ))
                            }
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= pagination.last_page}
                                className="rounded-md border border-gray-300 px-3 py-1 text-sm disabled:opacity-50 hover:bg-gray-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InventoriesOverview;
